import { ClientTransferRequestId } from "@sp-crm/core";
import { useMutation } from "@tanstack/react-query";
import { trpc } from "components/rpc/trpc";
import { fancyAlert } from "components/ui/fancy-alert";
import { fancyConfirm } from "components/ui/fancy-confirm";
import { SecondaryCompactButton } from "components/ui/secondary-button";
import { Spinner } from "components/ui/spinner";
import * as React from "react";
import { useDispatch } from "react-redux";
import { Actions } from "store/actions";
import { GlobalSuccessNotificationCreate } from "store/actions/global-notifications";

interface Props {
    clientTransferRequestId: ClientTransferRequestId;
    onTransferChanged: () => void;
}

export const OutgoingTransferActions: React.FC<Props> = props => {
    const { clientTransferRequestId, onTransferChanged } = props;
    const globalDispatch = useDispatch();
    const trpcContext = trpc.useTRPC();

    const cancelTransfer = useMutation(
        trpcContext.clientTransfer.cancel.mutationOptions(),
    );
    const resendTransfer = useMutation(
        trpcContext.clientTransfer.resend.mutationOptions(),
    );
    const busy = cancelTransfer.isPending || resendTransfer.isPending;

    const onCancelTransfer = React.useCallback(async () => {
        const confirm = await fancyConfirm(
            "Cancel transfer?",
            "This will cancel the transfer request. The client will not be transferred.",
            "Yes, cancel",
            "No, go back",
        );

        if (!confirm) {
            return;
        }

        try {
            await cancelTransfer.mutateAsync({ clientTransferRequestId });
        } catch {
            await fancyAlert(
                "Unable to cancel transfer",
                "This transfer request may have already been accepted, declined, or cancelled.",
            );
        }
        onTransferChanged();
    }, [cancelTransfer, onTransferChanged, clientTransferRequestId]);

    const onResendTransfer = React.useCallback(async () => {
        try {
            await resendTransfer.mutateAsync({ clientTransferRequestId });
        } catch {
            await fancyAlert(
                "Unable to resend transfer",
                "This transfer request may have already been accepted, declined, or cancelled.",
            );
            onTransferChanged();
            return;
        }
        const successNotification: GlobalSuccessNotificationCreate = {
            messageId: "resend-client-transfer",
            messageTitle: "Sent!",
            messageBody: "Client transfer notification sent.",
            type: Actions[Actions.GLOBAL_SUCCESS_NOTIFY],
        };
        globalDispatch(successNotification);
    }, [resendTransfer, clientTransferRequestId, globalDispatch, onTransferChanged]);

    return (
        <div className="flex flex-wrap items-center justify-end gap-1.5">
            {busy ? <Spinner /> : null}
            <SecondaryCompactButton
                type="button"
                onClick={onResendTransfer}
                disabled={busy}>
                Resend
            </SecondaryCompactButton>
            <SecondaryCompactButton
                type="button"
                onClick={onCancelTransfer}
                disabled={busy}>
                Cancel transfer
            </SecondaryCompactButton>
        </div>
    );
};
